// ═══════════════════════════════════════════════════════════════════════
// operators.ts — approved operator-door registry
// ═══════════════════════════════════════════════════════════════════════
// Maps an operator slug (the leading label of <slug>.guild.sloelabs.com, as
// returned by operatorSlugFromHostname) → its config. `company` is the only
// per-operator variable; `vertical` selects the chip taxonomy + demo set from
// VERTICALS. resolveOperator() is the single lookup the Studio uses to decide
// whether a door is a recognized operator (branded) or an unknown host (the
// standard Studio → null).
// ═══════════════════════════════════════════════════════════════════════

import { VERTICALS } from "./verticals";
import { labApiBase } from "@/src/lib/sloeAuthClient";

export interface OperatorConfig {
  company: string;
  vertical: keyof typeof VERTICALS;
}

export const OPERATORS: Record<string, OperatorConfig> = {
  test: { company: "Test Operator", vertical: "sports" },
  kayembe: { company: "Kayembe Energy", vertical: "oil-gas" },
  "kayembe-energy": { company: "Kayembe Energy", vertical: "oil-gas" },
};

// Module-level cache, seeded with the static map so resolveOperator works
// before (or without) a DB load. loadOperatorCache() layers approved DB
// operators on top at startup.
let operatorCache: Record<string, OperatorConfig> = { ...OPERATORS };

// Load APPROVED operators once and merge over the static map. Reads the
// tenant's public GET /api/operators/doors (slug, company, vertical only) — the
// Lab Data API has no anonymous role. Best-effort: any failure (network,
// server error) leaves the static map intact and never throws — bootstrap must
// not depend on this.
export async function loadOperatorCache(): Promise<void> {
  try {
    const r = await fetch(`${labApiBase()}/api/operators/doors`, { signal: AbortSignal.timeout(8000) });
    const body = r.ok ? await r.json() : null;
    const data: any[] | null = Array.isArray(body?.doors) ? body.doors : null;
    if (!data || data.length === 0) {
      if (!r.ok) console.warn("[operators] cache load failed:", r.status);
      return; // keep the static map
    }
    const dbApproved: Record<string, OperatorConfig> = {};
    for (const row of data as any[]) {
      const slug = row?.slug;
      const vertical = row?.vertical;
      // GUARD: require a real slug + a valid VERTICALS key, else skip the row
      // (DB values are loosely typed; a bad vertical would crash the callers).
      if (!slug || typeof slug !== "string") continue;
      // A missing/unrecognized vertical must NOT drop the operator from the
      // registry — an unregistered door loses its branding AND (before the
      // App.tsx host-only gate) rendered a blank LiveOS.
      const safeVertical =
        typeof vertical === "string" && vertical in VERTICALS
          ? (vertical as keyof typeof VERTICALS)
          : (Object.keys(VERTICALS)[0] as keyof typeof VERTICALS);
      dbApproved[slug] = {
        company: String(row.company ?? ""),
        vertical: safeVertical,
      };
    }
    operatorCache = { ...OPERATORS, ...dbApproved }; // DB on top, static survives
  } catch (e: any) {
    console.warn("[operators] cache load threw:", e?.message);
    // keep the static map
  }
}

export function resolveOperator(slug: string | null): OperatorConfig | null {
  if (!slug) return null;
  return operatorCache[slug] ?? null;
}
